import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const STALE_TIME = 2 * 60 * 1000;
const CACHE_TIME = 5 * 60 * 1000;

/**
 * Monthly P&L rows + KPI totals for the master/DAO store.
 * Backend: GET /api/analytics/transactions?year=&month=
 */
export const useTransactionsPnl = (year, month) => {
  return useQuery({
    queryKey: ["transactions-pnl", year, month],
    queryFn: async () => {
      const { data } = await axios.get("/api/analytics/transactions", {
        params: { year, month },
        metadata: { silent: true }, // access is gated by the page; don't toast 403s
      });
      return data; // { year, month, store, rows: [], totals: {} }
    },
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    keepPreviousData: true,
  });
};

/**
 * Save the inline "Notes" cell for one P&L row.
 *
 * Keyed by LISTING, not order — the P&L has one row per watch, and PX trade-in
 * rows have no order item to hang a note on. Its own endpoint rather than the
 * general listing update, which is locked once costs are finalised.
 */
export const saveTransactionsPnlNote = async (listingId, note) => {
  const { data } = await axios.put(`/api/analytics/transactions/${listingId}/note`, {
    pnl_note: note,
  });
  return data; // { listing_id, pnl_note }
};

/**
 * Write inline-edited P&L cells back to the listing.
 *
 * `patch` is a partial map of LISTING column names → values, exactly the shape
 * the row's `editable` object serves them in (money in minor units). Only keys
 * present are touched, so one cell can save without carrying the rest of the
 * row. The server re-checks the per-section grant for every key.
 */
export const saveTransactionsPnlFields = async (listingId, patch) => {
  const { data } = await axios.put(`/api/analytics/transactions/${listingId}/fields`, patch);
  return data; // { listing_id, editable }
};

/**
 * Trigger a browser download of the styled .xlsx. Uses axios (so the global
 * Authorization header is sent) and streams the response as a blob.
 */
export const downloadTransactionsPnlExcel = async (year, month) => {
  const res = await axios.get("/api/analytics/transactions/export", {
    params: { year, month },
    responseType: "blob",
  });

  const monthLabel = month ? String(month).padStart(2, "0") : "FULL";
  const fileName = `DAO-PnL-${year}-${monthLabel}.xlsx`;

  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Upload a supplier PO / invoice against one P&L row's listing.
 *
 * Lands in the listing's CONTRACTS ▸ Invoices bucket — the same place the
 * listing page reads and writes — so the file is visible (and removable) there,
 * and the PO/Invoice column resolves its link from whatever survives. Nothing is
 * stored on the row itself.
 *
 * Returns the row's three PO/Invoice fields for the new file, so the cell can
 * show its link before the month refetches.
 */
export const uploadTransactionsPnlDocument = async (listingId, file) => {
  const form = new FormData();
  form.append("file", file);

  const { data } = await axios.post(
    `/api/analytics/transactions/${listingId}/document`,
    form,
    // Same as the reconciliation board's uploader: axios must be told this is
    // multipart, and it fills in the boundary itself from the FormData.
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  return data; // { listing_id, po_invoice_file_id, po_invoice_source, po_invoice_url }
};
